/**
 * Denial Management API client for the standalone SPA.
 *
 * Single axios instance used for everything:
 *   - auth                 -> {base}/auth/...
 *   - org admin            -> {base}/users, /clients, /roles, /audit
 *   - denial CRUD          -> {base}/denials/...
 *   - data integrations    -> {base}/denial-integrations/...
 *   - process engine proxy -> {base}/process/...  (DM forwards to CM server-side)
 *
 * Auth: attaches the access JWT as a Bearer token. On a 401 it transparently
 * tries the refresh token once; if that fails the session is cleared and the
 * user is sent to /login.
 */
import axios from 'axios';
import { getDenialApiBaseUrl } from '../utils/env';
import { tokenStore } from '../auth/tokenStore';

const baseURL = getDenialApiBaseUrl();

export const dmClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 180000,
});

// A bare axios (no interceptors) for the refresh call, to avoid recursion.
const rawClient = axios.create({ baseURL, headers: { 'Content-Type': 'application/json' } });

dmClient.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshing = null;

function forceLogout() {
  tokenStore.clear();
  if (typeof window !== 'undefined' && !/\/login/.test(window.location.pathname)) {
    window.location.assign('/login');
  }
}

dmClient.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {};
    const status = error?.response?.status;

    if (status !== 401 || original._retried) {
      return Promise.reject(error);
    }

    const refreshToken = tokenStore.getRefresh();
    if (!refreshToken) {
      forceLogout();
      return Promise.reject(error);
    }

    try {
      if (!refreshing) {
        refreshing = rawClient
          .post('/auth/refresh', { refreshToken })
          .then((r) => r.data)
          .finally(() => { refreshing = null; });
      }
      const data = await refreshing;
      if (!data?.token) throw new Error('refresh failed');
      tokenStore.setTokens({ token: data.token, refreshToken: data.refreshToken });
      original._retried = true;
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${data.token}`;
      return dmClient(original);
    } catch (e) {
      forceLogout();
      return Promise.reject(error);
    }
  },
);

export default dmClient;
